import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { cardSurface } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import { Overline } from '@/components/atoms/Eyebrow';
import { formatNumberMaxDecimalsByLocale } from '@/utils/localeFormatters';

import type { MergeBlock, MergeCandidate } from '../../helpers/siteActions/mergeCandidates';
import { formatBoardWeight } from '../../helpers/weightText';

import styles from './MergeFishGroupModal.module.scss';

interface MergeCandidateListProps {
    candidates: MergeCandidate[];
    checked: ReadonlySet<number>;
    blockOf: (candidate: MergeCandidate) => MergeBlock | null;
    onToggle: (fishGroupId: number, checked: boolean) => void;
}

/** The site's live groups as rows to tick; the dialog's own group is ticked and locked. */
export default function MergeCandidateList(props: MergeCandidateListProps) {
    const { candidates, checked, blockOf, onToggle } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const locale = i18n.language;
    const placeholder = '–';
    const loading = t('siteActions.merge.loading');

    return (
        <div className={styles.candidates}>
            <div className={styles.columns} aria-hidden="true">
                <span />
                <Overline>{t('siteActions.merge.columns.pen')}</Overline>
                <Overline>{t('siteActions.merge.columns.fishGroup')}</Overline>
                <Overline>{t('siteActions.merge.columns.fish')}</Overline>
                <Overline>{t('siteActions.merge.columns.weight')}</Overline>
                <Overline>{t('siteActions.merge.columns.bioscope')}</Overline>
            </div>
            <ul className={styles.list}>
                {candidates.map((candidate) => {
                    const block = blockOf(candidate);
                    const isChecked = checked.has(candidate.fishGroupId);
                    // Not loaded yet: its species and stocking date aren't known, so it can't
                    // be ticked until they are.
                    const disabled = candidate.initial || block !== null || !candidate.loaded;
                    return (
                        <li
                            key={candidate.fishGroupId}
                            {...cardSurface(
                                {
                                    variant: 'outlined',
                                    radius: 'sm',
                                    padding: 'none',
                                    interactive: !disabled,
                                    selected: isChecked && !candidate.initial,
                                },
                                styles.candidate
                            )}>
                            <Checkbox
                                checked={isChecked}
                                disabled={disabled}
                                onCheckedChange={(on) => onToggle(candidate.fishGroupId, on)}
                                rowClassName={styles.candidateRow}
                                label={
                                    <span className={styles.cells}>
                                        <span>{candidate.pen.displayName}</span>
                                        <span className={styles.groupCell}>
                                            {candidate.name}
                                            {candidate.initial && (
                                                <Badge tone="info" size="xs">
                                                    {t('siteActions.merge.initial')}
                                                </Badge>
                                            )}
                                        </span>
                                        <span>
                                            {!candidate.loaded
                                                ? loading
                                                : candidate.fishCount !== null
                                                  ? formatNumberMaxDecimalsByLocale(
                                                        candidate.fishCount,
                                                        locale
                                                    )
                                                  : placeholder}
                                        </span>
                                        <span>
                                            {!candidate.loaded
                                                ? loading
                                                : candidate.grams !== null
                                                  ? formatBoardWeight(candidate.grams, locale)
                                                  : placeholder}
                                        </span>
                                        <span>
                                            {candidate.bioscopeIds.length > 0
                                                ? t('siteActions.bioscopeIds', {
                                                      ids: candidate.bioscopeIds.join(', '),
                                                  })
                                                : t('board.row.noBioscope')}
                                        </span>
                                        {block && (
                                            <span className={styles.block}>
                                                {t(`siteActions.merge.blocked.${block}`)}
                                            </span>
                                        )}
                                    </span>
                                }
                            />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
